// Per-connection user settings (archetype, automation, education, notifications).

import { apiFetch } from "./apiClient";
import { publishCompetitorTracking } from "./competitorTracking";
import {
    loadedConnectionQuery,
    refusedWriteError,
    type LoadedConnectionId,
} from "./loadedConnection";
import type {
    ConnectionSettings,
    ConnectionSettingsInput,
} from "../types/connectionSettings";

/** The open property's settings, and which property they were read for. */
export interface LoadedConnectionSettings {
    /** Null until something saves settings for the property. */
    settings: ConnectionSettings | null;
    /**
     * The property the settings were read for, which a write names back.
     * Undefined where the answer did not say: a service released before the
     * field names it only on stored settings.
     */
    connectionId: number | undefined;
}

export async function loadConnectionSettings(): Promise<LoadedConnectionSettings> {
    const res = await apiFetch("/api/connection-settings");
    if (!res.ok) throw new Error(`fetchConnectionSettings failed: ${res.status}`);
    const data = await res.json();
    const settings: ConnectionSettings | null = data.settings ?? null;
    const connectionId =
        typeof data.connectionId === "number"
            ? data.connectionId
            : typeof settings?.pmsConnectionId === "number"
              ? settings.pmsConnectionId
              : undefined;
    if (connectionId !== undefined) publishCompetitorTracking(connectionId, settings?.compsetEnabled === true);
    return { settings, connectionId };
}

/**
 * Save part of the open property's settings. `loadedConnectionId` names the
 * property the page read them for, and the service refuses the save with
 * `ActiveConnectionChangedError` once another one is open.
 */
export async function updateConnectionSettings(
    input: ConnectionSettingsInput,
    loadedConnectionId: LoadedConnectionId,
): Promise<ConnectionSettings> {
    const res = await apiFetch(`/api/connection-settings${loadedConnectionQuery(loadedConnectionId)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
    });
    if (!res.ok) throw await refusedWriteError(res, "updateConnectionSettings failed");
    const data = await res.json();
    const saved: ConnectionSettings | undefined = data.settings;
    if (typeof saved?.pmsConnectionId === "number") {
        publishCompetitorTracking(saved.pmsConnectionId, saved.compsetEnabled === true);
    }
    return data.settings;
}
